import type { ComponentPropsWithRef } from 'react';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

export interface CheckboxProps extends Omit<ComponentPropsWithRef<'input'>, 'type'> {
  label?: string;
}

/**
 * The real input stays in the DOM (hidden but focusable) so form semantics and
 * keyboard behaviour are native; the visible box is a sibling driven by
 * `peer-*` variants.
 */
export function Checkbox({ label, className, id, disabled, ...props }: CheckboxProps) {
  const control = (
    <span className="relative inline-flex shrink-0 items-center">
      <input
        id={id}
        type="checkbox"
        disabled={disabled}
        className="peer absolute h-4 w-4 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        {...props}
      />
      <span
        aria-hidden="true"
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-xs border border-border-strong bg-surface',
          /* Transparent to the pointer, so a click lands on the input beneath
             rather than on the artwork. Without this a bare `Checkbox` - one
             with no `label` to wrap it - is a control nobody can tick: the
             input is `opacity-0` and this box paints on top of it. Every
             caller happened to pass a label, so the trap sat unsprung until
             the first one that did not. */
          'pointer-events-none',
          'peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-content',
          /* The tick is a child of this box, not a sibling of the input, and
             `peer-checked:` compiles to a sibling selector - so it has to be
             driven from here. Put directly on the icon it never showed, and
             the box just turned solid blue with nothing in it. */
          '[&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-ring',
          'peer-disabled:bg-surface-muted peer-disabled:opacity-60',
          'transition-colors duration-100',
          className,
        )}
      >
        <Icon name="check" size={11} />
      </span>
    </span>
  );

  if (!label) {
    return control;
  }

  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-2 text-sm text-content',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      {control}
      {label}
    </label>
  );
}
